import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import {
  AdminEventTicketDetails,
  AdminEventTicketPurchase,
  callFetchAdminEventTicketDetails,
} from "../../../../api/eventTicketAdmin";
import LoadingSpinner from "../../../LoadingSpinner";
import ScrollableContainer from "../../components/ScrollableContainer";
import "../../components/Table.css";
import "../../components/Print.css";
import "./EventInfoPage.css";

function formatEventDateRange(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const datePart = startDate.toLocaleDateString("en-US", {
    dateStyle: "long",
  });
  const startTime = startDate.toLocaleTimeString("sv-SE", {
    timeStyle: "short",
  });
  const endTime = endDate.toLocaleTimeString("sv-SE", { timeStyle: "short" });
  return `${datePart} · ${startTime} – ${endTime}`;
}

function sortPurchasesByName(
  purchases: AdminEventTicketPurchase[],
): AdminEventTicketPurchase[] {
  return [...purchases].sort((a, b) =>
    a.buyerName.localeCompare(b.buyerName, "sv", { sensitivity: "base" }),
  );
}

const EventInfoDetailPage = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const printableRef = useRef<HTMLDivElement | null>(null);
  const [details, setDetails] = useState<AdminEventTicketDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handlePrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: details
      ? `${details.event.eventName} – participant checklist`
      : "Event participant checklist",
  });

  useEffect(() => {
    if (!eventId) return;

    setLoading(true);
    setError(null);
    callFetchAdminEventTicketDetails(eventId)
      .then(setDetails)
      .catch((err: Error) =>
        setError(err.message || "Failed to load event ticket info"),
      )
      .finally(() => setLoading(false));
  }, [eventId]);

  const sortedPurchases = useMemo(
    () => (details ? sortPurchasesByName(details.purchases) : []),
    [details],
  );

  const capacityText =
    details?.event.availableTicketCount != null
      ? `${details.ticketsSold} / ${details.event.availableTicketCount}`
      : String(details?.ticketsSold ?? 0);

  return (
    <div className="page-container">
      <div className="event-info-detail-header">
        <h1>{details?.event.eventName ?? "Event Info"}</h1>
      </div>

      {loading && <LoadingSpinner />}
      {!loading && error && <p className="error-message">{error}</p>}

      {!loading && !error && details && (
        <>
          <div className="event-info-summary">
            <p>
              <span className="bold">When: </span>
              {formatEventDateRange(
                details.event.eventStartTime,
                details.event.eventEndTime,
              )}
            </p>
            <p>
              <span className="bold">Location: </span>
              {details.event.eventLocation}
            </p>
            <p>
              <span className="bold">Tickets sold: </span>
              {capacityText}
            </p>
            <p>
              <span className="bold">Buyers: </span>
              {details.purchaseCount}
            </p>
          </div>

          <div className="btn-aligned-right-box event-info-print-actions">
            <button
              type="button"
              className="btn btn-aligned-right"
              onClick={handlePrint}
              disabled={details.purchases.length === 0}
            >
              <span className="only-big-screen">🖨️</span> Print or Save
            </button>
          </div>

          <div className="page-content event-info-page-content">
            {details.purchases.length === 0 ? (
              <p>No tickets sold yet for this event.</p>
            ) : (
              <div className="event-info-checklist-print">
                <div
                  ref={printableRef}
                  className="print order-table-container"
                >
                  <ScrollableContainer>
                    <table className="table event-info-checklist-table">
                      <thead className="sticky-table-head">
                        <tr>
                          <th className="th event-info-check-col"></th>
                          <th className="th event-info-col-name">Name</th>
                          <th className="th event-info-col-tickets-qty">Tickets</th>
                          <th className="th event-info-col-confirmation">
                            Confirmation ID
                          </th>
                          <th className="th event-info-comment-col">Comment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedPurchases.map((purchase) => (
                          <tr key={purchase.confirmationCode}>
                            <td className="td event-info-check-col">
                              <span
                                className="event-info-check-box"
                                aria-hidden="true"
                              />
                            </td>
                            <td className="td td-left event-info-col-name">
                              {purchase.buyerName}
                            </td>
                            <td className="td event-info-col-tickets-qty">
                              {purchase.quantity}
                            </td>
                            <td className="td event-info-col-confirmation">
                              {purchase.confirmationCode}
                            </td>
                            <td className="td td-left event-info-comment-col">
                              {purchase.comment || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollableContainer>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EventInfoDetailPage;
